/**
 * O que fazer com a fatura anterior e o modo da residual, por combinação (AF-4062).
 *
 * O backend resolve `INVOICE_PAYMENT_ACTION` (rota `/invoice-payment-action-settings`) e a tela só
 * consulta: a combinação é a natureza do título, a fonte escolhida e a liquidação. O que mora aqui é
 * o que a tela precisa para desenhar o campo nas três telas que tocam nele — o modal "Sinalizar
 * pagamento" (Títulos), o step 3 da Identificação e a coluna "Cobrança anterior" da tela de
 * Pagamentos —, para elas não divergirem.
 *
 * Os dois fallbacks que o backend não manda: fonte fora da tabela lê a de SACADO, e o baldinho
 * (`totalPayment` nulo) mantém a anterior sem mostrar nada, porque a cascata da operação já fecha
 * as faturas. Sem a configuração (rota fora do ar), vale a tela de antes: escolha na marcação, sem
 * padrão e sem residual.
 */
import {
    InvoicePaymentActionSettings, ParentAction, PaymentActionPromise, PaymentActionResolution,
    PromiseScope, ResidualInvoiceSettings,
} from './billingApi';
import { PARENT_ACTION_LABELS } from '../constants/paymentActionPromise';

const FALLBACK_SOURCE = 'SACADO';
const FALLBACK_REFERENCE = 'GUARANTEE';

export const NO_RESIDUAL: ResidualInvoiceSettings = {
    mode: 'NONE', visible: false, prefilled: false, withBoleto: false, keepsCollection: false,
};

export const LEGACY: PaymentActionResolution = {
    parentDefault: null, atMarking: true, atApproval: false, residual: NO_RESIDUAL,
};

const WATERFALL: PaymentActionResolution = {
    parentDefault: 'KEEP', atMarking: false, atApproval: false, residual: NO_RESIDUAL,
};

export const resolutionFor = (
    settings: InvoicePaymentActionSettings | null,
    businessReference: string | null | undefined,
    paymentSource: string | null | undefined,
    totalPayment: boolean | null,
): PaymentActionResolution => {
    if (totalPayment === null) return WATERFALL;
    if (!settings) return LEGACY;
    const byReference = settings.resolutions[businessReference || FALLBACK_REFERENCE];
    const bySource = byReference?.[paymentSource || ''] || byReference?.[FALLBACK_SOURCE];
    return bySource?.[totalPayment ? 'TOTAL' : 'PARTIAL'] || LEGACY;
};

/** A escolha de uma promessa gravada; `''` quando não há promessa. */
export const choiceOf = (promise?: string | null): ParentAction | '' => {
    if (promise === 'CANCEL' || promise === 'CANCEL_ALL') return 'CANCEL';
    if (promise === 'UNCOLLECT' || promise === 'UNCOLLECT_ALL') return 'UNCOLLECT';
    if (promise === 'NOTHING') return 'KEEP';
    return '';
};

/** A promessa da escolha no escopo da tela: `_ALL` no step 3, só a anterior no modal de Títulos. */
export const promiseOf = (choice: ParentAction, scope: PromiseScope): PaymentActionPromise => {
    if (choice === 'KEEP') return 'NOTHING';
    return scope === 'CHARGE' ? `${choice}_ALL` : choice;
};

/** O que o usuário escolheu, senão o padrão da combinação. */
export const effectiveChoice = (
    chosen: ParentAction | '', resolution: PaymentActionResolution,
): ParentAction | '' => chosen || resolution.parentDefault || '';

/**
 * O campo nas telas de marcação: se aparece e se é obrigatório. Onde a aprovação também decide, a
 * marcação pode deixar vazio. O ato único marca e aprova de uma vez, então mostra o campo de
 * qualquer um dos lados, e nele a escolha é sempre obrigatória.
 */
export const markingField = (resolution: PaymentActionResolution, singleAct = false) => {
    const shown = resolution.atMarking || (singleAct && resolution.atApproval);
    return { shown, required: shown && (singleAct || !resolution.atApproval) };
};

export type ApprovalCell =
    | { kind: 'NONE' }
    | { kind: 'TEXT'; choice: ParentAction }
    | { kind: 'SELECT'; value: ParentAction | '' };

/**
 * A coluna "Cobrança anterior" da tela de Pagamentos.
 *
 * "-" quando não há o que decidir: fatura já efetivada, baldinho, ou marcação que reaproveitou a
 * fatura (escopo NONE). Select onde a aprovação decide, com o marcado, senão o padrão — e também na
 * marcação sem promessa, qualquer que seja a configuração de agora: ela foi feita para a aprovação
 * decidir, e se o `controls` mudou depois ninguém decidiu ainda. Nos outros casos quem decidiu foi a
 * marcação, e a coluna só mostra a escolha.
 */
export const approvalCell = (
    invoice: any, settings: InvoicePaymentActionSettings | null,
): ApprovalCell => {
    const marking = invoice.paymentSignalled ? invoice.marking : null;
    if (!marking || marking.totalPayment === null || marking.paymentActionScope === 'NONE') {
        return { kind: 'NONE' };
    }
    const resolution = resolutionFor(
        settings, invoice.chargeScenario, marking.paymentSource, !!marking.totalPayment);
    const marked = choiceOf(marking.paymentActionPromise);
    if (resolution.atApproval || !marked) {
        return { kind: 'SELECT', value: effectiveChoice(marked, resolution) };
    }
    return { kind: 'TEXT', choice: marked };
};

const OUTCOME_ON_APPROVAL: Record<ParentAction, string> = {
    CANCEL: 'será cancelada',
    UNCOLLECT: 'deixará de ser cobrada',
    KEEP: 'continuará em aberto e sendo cobrada, ao lado da nova',
};

/**
 * O que acontece com a fatura anterior quando a escolha não está na tela de marcação: o padrão,
 * a decisão do aprovador ou a cascata do baldinho. Sem isto o usuário marca sem saber o que a
 * aprovação vai fazer por trás.
 */
export const previousInvoiceOutcome = (
    resolution: PaymentActionResolution, totalPayment: boolean | null,
): string => {
    if (totalPayment === null) {
        return 'Esta fatura continua como está: a amortização distribui o valor pelas parcelas da '
            + 'operação, e a cascata fecha as faturas das parcelas que cobrir.';
    }
    if (resolution.atApproval) {
        const suggestion = resolution.parentDefault
            ? ` (sugestão: ${PARENT_ACTION_LABELS[resolution.parentDefault]})` : '';
        return `O que fazer com esta fatura será decidido na aprovação, na tela de Pagamentos${suggestion}.`;
    }
    const outcome = OUTCOME_ON_APPROVAL[resolution.parentDefault || 'KEEP'];
    return `Quando o pagamento for aprovado, esta fatura ${outcome}.`;
};
